import LegalPage from '../../components/legal/LegalPage';
import './Downloads.css';

const VERSION = '1.0.0';

const DOWNLOADS = {
  notesWindows: `/downloads/notes/latest/MySimpleDB-Notes-Setup-${VERSION}.exe`,
  notesMacArm: `/downloads/notes/latest/MySimpleDB-Notes-${VERSION}-arm64.dmg`,
  notesMacIntel: `/downloads/notes/latest/MySimpleDB-Notes-${VERSION}-x64.dmg`,

  desktopWindows: `/downloads/desktop/latest/MySimpleDB-Setup-${VERSION}.exe`,
  desktopMacArm: `/downloads/desktop/latest/MySimpleDB-${VERSION}-arm64.dmg`,
  desktopMacIntel: `/downloads/desktop/latest/MySimpleDB-${VERSION}-x64.dmg`,
};

// SAMPLE DESKTOP VALUES ONLY — replace with the SHA-256 values from each release build.
const CHECKSUMS = {
  notesWindows:
    '9F6E0AFDE38CF73A958A4EA8BDE088A9A69C5D5D3EA63EAFAD76EDC2316941EC',
  notesMacArm:
    'acbe5085d0b3a337ad557e192e7b9edc3012471b42c3b48cd914bc717ee3e491',
  notesMacIntel:
    '9a5e46d5289d57d52f32d896fdd502f1e1fc66db13cc4b9aa01b12a144921293',
  desktopWindows:
    'ED5862EB2A98AAABF5E3D3E1FD12404B5FEC613A9AC224E09DA5E495C1DAB9AF',
  desktopMacArm:
    'b2cf054a687bc714c55c31e65c749396e68108f3b5934cf4f69cf81809d15108',
  desktopMacIntel:
    'e5d014af389953845e7faaae1857d53c66cad30c0dfea2aea71fbdb8f5f317f1',
};

export default function Downloads() {
  return (
    <LegalPage title="Download MySimpleDB">
      <div className="content downloads-content">
        <p className="downloads-intro">
          Download MySimpleDB Notes or MySimpleDB Desktop for Windows or Mac.
          Choose the application and version that matches your computer.
        </p>

        <div className="downloads-version">
          Current version: <strong>{VERSION}</strong>
        </div>

        <section className="downloads-product">
          <div className="downloads-product-heading">
            <h2>MySimpleDB Notes</h2>
            <p>Sticky notes synchronized with your MySimpleDB account.</p>
          </div>
        </section>

        <section className="downloads-section">
          <h2>Windows</h2>

          <div className="download-card">
            <div className="download-card-heading">
              <div className="download-platform-icon windows-icon">⊞</div>

              <div>
                <h3>MySimpleDB Notes for Windows</h3>
                <p>Compatible with Windows 10 and Windows 11 (64-bit).</p>
              </div>
            </div>

            <div className="download-details">
              <span>Windows 10</span>
              <span>Windows 11</span>
              <span>64-bit</span>
            </div>

            <a
              className="download-button"
              href={DOWNLOADS.notesWindows}
              download
            >
              Download for Windows
            </a>

            <div className="download-filename">
              MySimpleDB-Notes-Setup-{VERSION}.exe
            </div>
            <div className="download-checksum">
              <div className="checksum-label">SHA-256 checksum</div>
              <code>{CHECKSUMS.notesWindows}</code>
            </div>
          </div>
        </section>

        <section className="downloads-section">
          <h2>Mac</h2>

          <p className="section-description">
            Choose the version that matches your Mac processor.
          </p>

          <div className="mac-downloads">
            <div className="download-card">
              <div className="download-card-heading">
                <div className="download-platform-icon mac-icon"></div>

                <div>
                  <h3>Mac (Apple Silicon)</h3>
                  <p>For Macs with an Apple M-series processor.</p>
                </div>
              </div>

              <div className="download-details">
                <span>Apple Silicon</span>
                <span>M1, M2, M3, M4 and newer</span>
                <span>ARM64</span>
              </div>

              <a
                className="download-button"
                href={DOWNLOADS.notesMacArm}
                download
              >
                Download for Apple Silicon
              </a>

              <div className="download-filename">
                MySimpleDB-Notes-{VERSION}-arm64.dmg
              </div>
              <div className="download-checksum">
                <div className="checksum-label">SHA-256 checksum</div>
                <code>{CHECKSUMS.notesMacArm}</code>
              </div>
            </div>

            <div className="download-card">
              <div className="download-card-heading">
                <div className="download-platform-icon mac-icon"></div>

                <div>
                  <h3>Mac (Intel)</h3>
                  <p>For Macs with an Intel processor.</p>
                </div>
              </div>

              <div className="download-details">
                <span>Intel</span>
                <span>x64</span>
              </div>

              <a
                className="download-button"
                href={DOWNLOADS.notesMacIntel}
                download
              >
                Download for Mac (Intel)
              </a>

              <div className="download-filename">
                MySimpleDB-Notes-{VERSION}-x64.dmg
              </div>
              <div className="download-checksum">
                <div className="checksum-label">SHA-256 checksum</div>
                <code>{CHECKSUMS.notesMacIntel}</code>
              </div>
            </div>
          </div>

          <div className="mac-help">
            <strong>Not sure which Mac you have?</strong>
            <p>
              Open the Apple menu  and select <strong>About This Mac</strong>.
              If it shows an Apple M-series chip, choose Apple Silicon. If it
              shows an Intel processor, choose Mac (Intel).
            </p>
          </div>
        </section>

        <section className="downloads-product downloads-product--desktop">
          <div className="downloads-product-heading">
            <h2>MySimpleDB Desktop</h2>
            <p>
              Access MySimpleDB in a dedicated desktop app and synchronize
              supported attachments with files on your computer.
            </p>
          </div>
        </section>

        <section className="downloads-section">
          <h2>Windows</h2>

          <div className="download-card">
            <div className="download-card-heading">
              <div className="download-platform-icon windows-icon">⊞</div>
              <div>
                <h3>MySimpleDB Desktop for Windows</h3>
                <p>Compatible with Windows 10 and Windows 11 (64-bit).</p>
              </div>
            </div>

            <div className="download-details">
              <span>Windows 10</span>
              <span>Windows 11</span>
              <span>64-bit</span>
            </div>

            <a
              className="download-button"
              href={DOWNLOADS.desktopWindows}
              download
            >
              Download Desktop for Windows
            </a>

            <div className="download-filename">
              MySimpleDB-Desktop-Setup-{VERSION}.exe
            </div>
            <div className="download-checksum">
              <div className="checksum-label">SHA-256 checksum</div>
              <code>{CHECKSUMS.desktopWindows}</code>
            </div>
          </div>
        </section>

        <section className="downloads-section">
          <h2>Mac</h2>

          <p className="section-description">
            Choose the version that matches your Mac processor.
          </p>

          <div className="mac-downloads">
            <div className="download-card">
              <div className="download-card-heading">
                <div className="download-platform-icon mac-icon"></div>
                <div>
                  <h3>MySimpleDB Desktop — Apple Silicon</h3>
                  <p>For Macs with an Apple M-series processor.</p>
                </div>
              </div>

              <div className="download-details">
                <span>Apple Silicon</span>
                <span>M1, M2, M3, M4 and newer</span>
                <span>ARM64</span>
              </div>

              <a
                className="download-button"
                href={DOWNLOADS.desktopMacArm}
                download
              >
                Download Desktop for Apple Silicon
              </a>

              <div className="download-filename">
                MySimpleDB-Desktop-{VERSION}-arm64.dmg
              </div>
              <div className="download-checksum">
                <div className="checksum-label">SHA-256 checksum</div>
                <code>{CHECKSUMS.desktopMacArm}</code>
              </div>
            </div>

            <div className="download-card">
              <div className="download-card-heading">
                <div className="download-platform-icon mac-icon"></div>
                <div>
                  <h3>MySimpleDB Desktop — Intel</h3>
                  <p>For Macs with an Intel processor.</p>
                </div>
              </div>

              <div className="download-details">
                <span>Intel</span>
                <span>x64</span>
              </div>

              <a
                className="download-button"
                href={DOWNLOADS.desktopMacIntel}
                download
              >
                Download Desktop for Mac (Intel)
              </a>

              <div className="download-filename">
                MySimpleDB-Desktop-{VERSION}-x64.dmg
              </div>
              <div className="download-checksum">
                <div className="checksum-label">SHA-256 checksum</div>
                <code>{CHECKSUMS.desktopMacIntel}</code>
              </div>
            </div>
          </div>
        </section>

        <section className="downloads-section downloads-security">
          <h2>Official MySimpleDB Downloads</h2>

          <p>
            Download MySimpleDB applications only from the official
            mysimpledb.com website.
          </p>

          <ul>
            <li>Downloads are delivered securely over HTTPS.</li>
            <li>No advertising or bundled third-party software.</li>
            <li>Installers are distributed directly by MySimpleDB.</li>
          </ul>
        </section>

        <section className="downloads-section checksum-verification">
          <h2>Verify Your Download</h2>
          <p className="section-description">
            Each installer includes a SHA-256 checksum above. After downloading,
            compare the checksum of your file with the value shown on this page.
            An exact match verifies that the file was downloaded intact.
          </p>

          <div className="checksum-instructions">
            <div>
              <h3>Windows (PowerShell)</h3>
              <code className="checksum-command">
                Get-FileHash .\&lt;downloaded-installer&gt; -Algorithm SHA256
              </code>
              <p className="checksum-note">
                Use the exact downloaded filename, such as{' '}
                <code>MySimpleDB-Notes-Setup-{VERSION}.exe</code> or{' '}
                <code>MySimpleDB-Desktop-Setup-{VERSION}.exe</code>.
              </p>
            </div>

            <div>
              <h3>Mac (Terminal)</h3>
              <code className="checksum-command">
                shasum -a 256 &lt;downloaded-dmg&gt;
              </code>
              <p className="checksum-note">
                Use the exact downloaded filename shown above.
              </p>
            </div>
          </div>
        </section>

        <section className="downloads-section">
          <h2>Installation</h2>
          <div className="installation-grid">
            <div>
              <h3>Windows</h3>
              <ol>
                <li>Download the Windows installer.</li>
                <li>Double-click downloaded .exe file.</li>
                <li>Launch the downloaded MySimpleDB app and sign in.</li>
              </ol>
            </div>

            <div>
              <h3>Mac</h3>
              <ol>
                <li>Download the Apple Silicon or Intel .dmg for your Mac.</li>
                <li>Open the downloaded disk image.</li>
                <li>Move the MySimpleDB app to Applications.</li>
                <li>Launch the app and sign in.</li>
              </ol>
            </div>
          </div>{' '}
        </section>

        <section className="downloads-section">
          <h2>Getting Started</h2>

          <div className="installation-grid">
            <div>
              <h3>Windows</h3>
              <ul>
                <li>Sign in with your MySimpleDB account.</li>
                <li>
                  <strong>
                    Press Ctrl+Shift+M to open the MySimpleDB Notes menu.
                  </strong>{' '}
                </li>
                <li>
                  Use the Menu to access Settings, Logout, and other options.
                </li>
                <li>
                  Create notes in the MyNotes section of the MySimpleDB Web App.
                  They will automatically appear in MySimpleDB Notes.
                </li>
              </ul>
            </div>

            <div>
              <h3>Mac</h3>
              <ul>
                <li>Sign in with your MySimpleDB account.</li>
                <li>Use the macOS menu bar to access application commands.</li>
                <li>
                  Create notes in the MyNotes section of the MySimpleDB Web App.
                  They will automatically appear in MySimpleDB Notes.
                </li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </LegalPage>
  );
}
